import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ArrowRight, Loader2, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * /buy-leads/order-success
 *
 * Two arrival paths:
 *   - Stripe success → ?session_id=cs_live_xxx — poll /lead-packs/status until paid
 *   - Bank transfer  → ?order_id=xxx&method=bank_transfer — show "we emailed invoice"
 *
 * iter86: if the user is already signed in (or arrives with ?signed_in=1)
 * we link them straight into `/dashboard/billing` where their pending invoice
 * is now visible, rather than dumping them at /login.
 */
export default function BuyLeadsOrderSuccess() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const sessionId = params.get("session_id");
  const orderId = params.get("order_id");
  const method = params.get("method");
  const signedIn = params.get("signed_in") === "1";
  const [status, setStatus] = useState("checking"); // checking | paid | unpaid | bank
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (method === "bank_transfer") { setStatus("bank"); return; }
    if (!sessionId) { setStatus("unpaid"); return; }
    let cancel = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/lead-packs/status/${sessionId}`);
        if (cancel) return;
        if (data.payment_status === "paid") setStatus("paid");
        else if (tries > 12) setStatus("unpaid");
        else setTimeout(() => setTries((t) => t + 1), 2000);
      } catch (_) {
        if (!cancel) setStatus("unpaid");
      }
    };
    poll();
    return () => { cancel = true; };
  }, [sessionId, tries, method]);

  // If signed in (or arrived from an authed checkout), prefer routing into the
  // dashboard rather than asking them to log in again.
  const dashHref = (user || signedIn) ? "/dashboard/billing" : "/login";
  const dashLabel = (user || signedIn) ? "Open your billing" : "Open your dashboard";

  return (
    <div className="min-h-[70vh] flex items-center">
      <div className="mx-auto max-w-xl px-4 py-20 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto size-10 text-[#0055FF] animate-spin" />
            <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-900">Confirming your payment…</h1>
            <p className="mt-3 text-[14px] text-slate-600">This usually takes a few seconds. Don&apos;t close this tab.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto size-12 text-emerald-500" strokeWidth={1.6} />
            <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-900">Order confirmed.</h1>
            <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
              Your invoice is on the way to your inbox. Our compliance team will be in touch within 24 hours to verify delivery — then your leads start flowing live to your CRM.
            </p>
            <div className="mt-8 flex justify-center gap-2.5">
              <Link to={dashHref}><Button className="rounded-full h-11 px-5" data-testid="success-go-dashboard">{dashLabel} <ArrowRight className="size-4" /></Button></Link>
            </div>
          </>
        )}
        {status === "bank" && (
          <>
            <FileText className="mx-auto size-12 text-[#0055FF]" strokeWidth={1.6} />
            <h1 className="mt-6 text-3xl font-semibold tracking-tight text-slate-900">Invoice emailed.</h1>
            <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">
              Your PDF invoice with our Monzo bank details is on its way to your inbox. We&apos;ll activate your leads as soon as funds clear — usually same-day for UK transfers before 3pm.
            </p>
            {(user || signedIn) && (
              <p className="mt-2 text-[13px] text-slate-500">
                You can also <Link to="/dashboard/billing" className="text-[#0055FF] hover:underline">view & download the invoice in your billing tab</Link> any time.
              </p>
            )}
            <div className="mt-8 flex justify-center gap-2.5">
              <Link to="/"><Button variant="outline" className="rounded-full h-11 px-5">Back to home</Button></Link>
              <Link to={dashHref}><Button className="rounded-full h-11 px-5">{dashLabel} <ArrowRight className="size-4" /></Button></Link>
            </div>
          </>
        )}
        {status === "unpaid" && (
          <>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">We couldn&apos;t confirm payment.</h1>
            <p className="mt-3 text-[14px] text-slate-600">
              Don&apos;t worry — Stripe may still be processing. Refresh in a moment, or {orderId && `quote order ${orderId.slice(0, 8)}…`} to <Link to="/contact" className="text-[#0055FF] hover:underline">our team</Link>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
